import type { GenerationDetailsDto } from '@str/api/types';
import { useEffect, useState } from 'react';

const dollars = (value: number | null | undefined) =>
  value == null ? 'Unknown' : `$${value.toFixed(4)}`;

/** Detailed diagnostics are loaded on demand and are authorized again by the API. */
export function GenerationDetails({
  postId,
  revision,
  owner,
}: {
  postId: string;
  revision: string;
  owner: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ key: string; data: GenerationDetailsDto } | null>(null);
  const [error, setError] = useState<{ key: string; message: string } | null>(null);
  const key = `${postId}:${revision}`;
  useEffect(() => {
    if (!owner || !open) return;
    const controller = new AbortController();
    void fetch(`/api/posts/${encodeURIComponent(postId)}/generation-details`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Generation details could not load.');
        const value: { details: GenerationDetailsDto } = await response.json();
        if (!controller.signal.aborted) {
          setResult({ key, data: value.details });
          setError(null);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError({ key, message: 'Generation details could not load.' });
      });
    return () => controller.abort();
  }, [key, open, owner, postId]);
  if (!owner) return null;
  const data = result?.key === key ? result.data : null;
  return (
    <section className="nx-generation-details" aria-label="Owner generation diagnostics">
      <button type="button" className="nx-chip" aria-expanded={open} onClick={() => setOpen(!open)}>
        Generation details
      </button>
      {open && (
        <div style={{ marginTop: 12, overflowWrap: 'anywhere', fontSize: 12 }}>
          {error?.key === key ? (
            <p role="alert">{error.message}</p>
          ) : !data ? (
            <p role="status">Loading details…</p>
          ) : data.attempts.length === 0 ? (
            <p>No recorded generation attempts.</p>
          ) : (
            data.attempts.map((attempt) => (
              <article key={attempt.id} style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}>
                <p>
                  <strong>
                    {attempt.plan?.operation.replaceAll('_', ' ') ?? 'Legacy generation'}
                  </strong>{' '}
                  · {attempt.status}
                </p>
                <p>
                  {attempt.acceptance === 'unjudged'
                    ? 'Unjudged'
                    : attempt.acceptance === 'accepted'
                      ? 'Accepted'
                      : 'Rejected'}
                </p>
                {attempt.plan && (
                  <>
                    <p>{attempt.plan.originalRequest}</p>
                    <p>
                      Requested changes:{' '}
                      {attempt.plan.requestedChanges.join('; ') || 'None recorded'}
                    </p>
                    <p>Preserve: {attempt.plan.preserve.join('; ') || 'None recorded'}</p>
                    <p>Sources: {attempt.plan.sourceMediaIds.join(', ') || 'None'}</p>
                    <p>Original asset: {attempt.plan.renderedAssetId ?? 'None'}</p>
                    <p>Maximum attempts per paid operation: {attempt.plan.maxPaidAttempts}</p>
                  </>
                )}
                <p>
                  Base version: {attempt.baseVersionId ?? 'None'}
                  <br />
                  Candidate: {attempt.versionId ?? 'None'}
                </p>
                <p>Estimated cost: {dollars(attempt.estimatedCostUsd)}</p>
                <p>Billed cost: {dollars(attempt.billedCostUsd)}</p>
                {attempt.trace.length === 0 && <p>No stage trace recorded.</p>}
                {attempt.trace.map((stage, i) => (
                  <div
                    key={`${stage.stage}:${stage.startedAt}:${i}`}
                    style={{ paddingLeft: 10, borderLeft: '2px solid #ddd', marginTop: 10 }}
                  >
                    <strong>
                      {stage.stage} · {stage.outcome}
                    </strong>
                    <p>{stage.requestedModel ?? 'Unknown model'}</p>
                    <p>Reported model: {stage.reportedModel ?? 'Unknown'}</p>
                    <p>
                      {stage.provider ?? 'Unknown provider'} ·{' '}
                      {stage.endpointVariant ?? 'Unknown endpoint'}
                    </p>
                    <p>
                      Tokens in/out: {stage.inputTokens ?? 'Unknown'} /{' '}
                      {stage.outputTokens ?? 'Unknown'}
                    </p>
                    <p>
                      Estimated: {dollars(stage.estimatedCostUsd)} · Billed:{' '}
                      {dollars(stage.billedCostUsd)}
                    </p>
                    {stage.providerJobId && <p>Provider request: {stage.providerJobId}</p>}
                  </div>
                ))}
              </article>
            ))
          )}
        </div>
      )}
    </section>
  );
}
